import "./style.css";

/* ------------------------------------------------------------------ *
 * CalendarMonth — one month of a pick-a-day calendar, plus the date
 * helpers that go with it.
 *
 * Extracted from overviewPage.js so other screens can reuse it. The
 * overview composes two of these into its range popover; the work order
 * list uses a single one to jump its day rail to any date.
 *
 * Timestamps in and out are local-midnight epoch ms (startOfDay).
 * `from`/`to` mark the selected range — pass the same stamp for both to
 * highlight a single day. Month navigation is the caller's: pass
 * onPrev/onNext to render the arrows.
 * ------------------------------------------------------------------ */

export const DAY_MS = 86400000;
const DOW = ["M", "T", "W", "T", "F", "S", "S"];

export function startOfDay(t) {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function shiftDays(t, days) {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days).getTime();
}

/* Monday-first offset for the 1st of a month. */
export function leadingBlanks(year, monthIndex) {
  return (new Date(year, monthIndex, 1).getDay() + 6) % 7;
}

export function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function shiftMonth({ year, monthIndex }, delta) {
  const d = new Date(year, monthIndex + delta, 1);
  return { year: d.getFullYear(), monthIndex: d.getMonth() };
}

export function monthOf(t) {
  const d = new Date(t);
  return { year: d.getFullYear(), monthIndex: d.getMonth() };
}

export function CalendarMonth({ year, monthIndex, from, to, onPick, onPrev, onNext }) {
  const label = new Date(year, monthIndex, 1).toLocaleString("en-AU", {
    month: "long",
    year: "numeric",
  });
  const blanks = leadingBlanks(year, monthIndex);
  const days = daysInMonth(year, monthIndex);
  /* today is always marked, picked or not, so the calendar shows at a
     glance where today sits against the chosen day */
  const today = startOfDay(Date.now());

  return (
    <div className="rf-cal-month">
      <div className="rf-cal-month-head">
        {onPrev && (
          <button type="button" className="rf-cal-nav" onClick={onPrev} aria-label="Previous month">
            ‹
          </button>
        )}
        <span>{label}</span>
        {onNext && (
          <>
            <span className="rf-cal-spacer" />
            <button type="button" className="rf-cal-nav" onClick={onNext} aria-label="Next month">
              ›
            </button>
          </>
        )}
      </div>
      <div className="rf-cal-grid">
        {DOW.map((d, i) => (
          <span key={`dow-${i}`} className="rf-cal-dow">
            {d}
          </span>
        ))}
        {Array.from({ length: blanks }).map((_, i) => (
          <button key={`blank-${i}`} type="button" className="rf-cal-day is-empty" disabled />
        ))}
        {Array.from({ length: days }).map((_, i) => {
          const day = i + 1;
          const stamp = new Date(year, monthIndex, day).getTime();
          const isEdge = stamp === from || stamp === to;
          const inRange = from && to && stamp > from && stamp < to;
          const isToday = stamp === today;
          return (
            <button
              key={`day-${day}`}
              type="button"
              className={`rf-cal-day${isEdge ? " is-edge" : inRange ? " in-range" : ""}${
                isToday ? " is-today" : ""
              }`}
              aria-current={isToday ? "date" : undefined}
              title={isToday ? "Today" : undefined}
              onClick={() => onPick(stamp)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default CalendarMonth;
